// FILE: src/components/achievements/BadgeLightbox.js
//
// BadgeLightbox — modal overlay showing a single achievement badge at large
// size with its title, description, and earned state. Opened from the
// Milestones grid (badge art is hard to appreciate at grid size).
//
// Presentational + controlled: caller passes the catalog item (or null) and
// an onClose handler. Closes on backdrop click, the X button, or Escape.
//
// Functional component, hooks only. No Redux.

import React, { useEffect } from 'react';
import AchievementBadge from './AchievementBadge';
import styles from './BadgeLightbox.module.css';

export default function BadgeLightbox({ item, onClose }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      data-testid="badge-lightbox-backdrop"
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={`${item.title} badge`}
        onClick={(e) => e.stopPropagation()}
        data-testid="badge-lightbox"
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close badge view"
          data-testid="badge-lightbox-close"
        >&times;</button>
        <AchievementBadge
          iconKey={item.icon}
          earned={!!item.earned}
          title={item.title}
          decorative={false}
          size={300}
        />
        <div className={styles.meta}>
          <div className={styles.titleRow}>
            <span className={styles.title}>{item.title}</span>
            {item.earned && <span className={styles.earnedChip}>Earned</span>}
          </div>
          {item.description && <p className={styles.desc}>{item.description}</p>}
          {item.earned && item.earned_at && (
            <p className={styles.date}>
              {new Date(item.earned_at).toLocaleDateString(undefined,
                { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
          {!item.earned && <p className={styles.date}>Not earned yet</p>}
        </div>
      </div>
    </div>
  );
}
